import React from 'react';
import { Globe as GlobeIcon, PhoneOff as PhoneOffIcon, AlertTriangle as AlertTriangleIcon } from 'lucide-react';

/**
 * Offline Banner Component
 * Shows when user loses internet connectivity
 */
export const OfflineBanner = ({ isVisible = true, onRetry = null }) => {
  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: '#8E3628',
      color: 'white',
      padding: '12px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      zIndex: 9999,
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      animation: 'slideDown 0.3s ease-out'
    }}>
      <span style={{ fontSize: '20px' }}><PhoneOffIcon size="1em" aria-hidden="true" /></span>
      <span style={{ fontWeight: '600' }}>
        You are currently offline. Please check your internet connection.
      </span>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            backgroundColor: 'white',
            color: '#8E3628',
            border: 'none',
            padding: '6px 16px',
            borderRadius: '6px',
            fontWeight: '600',
            cursor: 'pointer',
            marginLeft: '8px'
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
};

/**
 * Error Display Component
 * Shows user-friendly error messages with retry option
 */
export const ErrorDisplay = ({ 
  error, 
  onRetry = null, 
  fullScreen = false,
  showIcon = true,
  title = null,
  message = null,
  actionText = 'Try Again'
}) => {
  if (!error) return null;

  const getErrorInfo = () => {
    // Handle enhanced error object from API
    if (error.errorType) {
      return {
        icon: getIconForType(error.errorType),
        title: error.errorTitle || title || 'Error',
        message: error.errorMessage || message || 'Something went wrong',
        canRetry: error.canRetry
      };
    }

    // Handle standard error
    if (error.isNetworkError) {
      return {
        title: title || 'Connection Error',
        message: message || 'Unable to connect to the server. Please check your internet connection.',
        canRetry: true
      };
    }

    if (error.isTimeoutError) {
      return {
        title: title || 'Request Timeout',
        message: message || 'The server is taking too long to respond. Please try again.',
        canRetry: true
      };
    }

    return {
      title: title || 'Error',
      message: message || error.message || 'Something went wrong',
      canRetry: true
    };
  };

  const getIconForType = (type) => {
    const icons = {
      'NETWORK_ERROR': '',
      'TIMEOUT_ERROR': '',
      'SERVER_ERROR': '',
      'AUTH_ERROR': '',
      'VALIDATION_ERROR': '',
      'NOT_FOUND_ERROR': '',
      'RATE_LIMIT_ERROR': '',
      'UNKNOWN_ERROR': ''
    };
    return icons[type] || '';
  };

  const errorInfo = getErrorInfo();

  const containerStyle = fullScreen ? {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '40px',
    textAlign: 'center'
  } : {
    padding: '24px',
    backgroundColor: '#F9EEEB',
    borderRadius: '12px',
    border: '1px solid #E5B3A9',
    textAlign: 'center'
  };

  return (
    <div style={containerStyle}>
      {showIcon && (
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>
          <AlertTriangleIcon size="1em" aria-hidden="true" />
        </div>
      )}
      <h3 style={{ 
        margin: '0 0 8px 0', 
        fontSize: '20px', 
        fontWeight: '700',
        color: '#5F241B'
      }}>
        {errorInfo.title}
      </h3>
      <p style={{ 
        margin: '0 0 20px 0', 
        color: '#481B14',
        fontSize: '14px',
        maxWidth: '400px'
      }}>
        {errorInfo.message}
      </p>
      {onRetry && errorInfo.canRetry && (
        <button
          onClick={onRetry}
          style={{
            backgroundColor: '#8E3628',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            transition: 'background-color 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#772D21'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#8E3628'}
        >
          {actionText}
        </button>
      )}
    </div>
  );
};

/**
 * Loading with Error Fallback Component
 */
export const LoadingWithError = ({
  loading,
  error,
  onRetry,
  children,
  loadingComponent = null,
  errorComponent = null
}) => {
  if (loading) {
    return loadingComponent || (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #E3DFD3',
          borderTopColor: '#1A3E2A',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <span style={{ color: '#5F6B61', fontSize: '14px' }}>Loading...</span>
      </div>
    );
  }

  if (error) {
    return errorComponent || (
      <ErrorDisplay error={error} onRetry={onRetry} fullScreen />
    );
  }

  return children;
};

/**
 * Connection Status Indicator
 */
export const ConnectionStatus = ({ isOnline, showWhenOnline = false }) => {
  if (isOnline && !showWhenOnline) return null;

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 12px',
      borderRadius: '16px',
      fontSize: '12px',
      fontWeight: '600',
      backgroundColor: isOnline ? '#E1E9E2' : '#F9EEEB',
      color: isOnline ? '#0F2619' : '#5F241B'
    }}>
      <span style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: isOnline ? '#1A3E2A' : '#A13F2F',
        animation: isOnline ? 'none' : 'pulse 2s infinite'
      }} />
      {isOnline ? 'Online' : 'Offline'}
    </div>
  );
};

/**
 * Retry Button Component
 */
export const RetryButton = ({ 
  onClick, 
  loading = false, 
  text = 'Retry',
  fullWidth = false,
  variant = 'primary' // 'primary', 'secondary', 'outline'
}) => {
  const getStyles = () => {
    const base = {
      padding: '10px 20px',
      borderRadius: '8px',
      fontWeight: '600',
      cursor: loading ? 'not-allowed' : 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      opacity: loading ? 0.7 : 1,
      width: fullWidth ? '100%' : 'auto'
    };

    switch (variant) {
      case 'primary':
        return {
          ...base,
          backgroundColor: '#1A3E2A',
          color: 'white',
          border: 'none'
        };
      case 'secondary':
        return {
          ...base,
          backgroundColor: '#EEEBE1',
          color: '#36433B',
          border: '1px solid #D2CDBF'
        };
      case 'outline':
        return {
          ...base,
          backgroundColor: 'transparent',
          color: '#1A3E2A',
          border: '2px solid #1A3E2A'
        };
      default:
        return base;
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={getStyles()}
    >
      {loading ? (
        <>
          <span style={{
            width: '16px',
            height: '16px',
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          Retrying...
        </>
      ) : (
        <>
          {text}
        </>
      )}
    </button>
  );
};

/**
 * Network Error Card
 * Full-featured error display for major errors
 */
export const NetworkErrorCard = ({
  title = 'Connection Error',
  message = 'Unable to connect to the server',
  onRetry = null,
  onGoBack = null,
  showTips = true
}) => {
  return (
    <div style={{
      maxWidth: '500px',
      margin: '40px auto',
      padding: '32px',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '64px', marginBottom: '20px' }}><GlobeIcon size="1em" aria-hidden="true" /></div>
      <h2 style={{ 
        margin: '0 0 12px 0', 
        fontSize: '24px', 
        fontWeight: '700',
        color: '#24332A'
      }}>
        {title}
      </h2>
      <p style={{ 
        margin: '0 0 24px 0', 
        color: '#5F6B61',
        fontSize: '16px',
        lineHeight: '1.5'
      }}>
        {message}
      </p>

      {showTips && (
        <div style={{
          backgroundColor: '#F5F3EB',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          textAlign: 'left'
        }}>
          <p style={{ 
            margin: '0 0 12px 0', 
            fontWeight: '600', 
            color: '#36433B',
            fontSize: '14px'
          }}>
            Try the following:
          </p>
          <ul style={{ 
            margin: 0, 
            paddingLeft: '20px', 
            color: '#5F6B61',
            fontSize: '13px',
            lineHeight: '1.8'
          }}>
            <li>Check your internet connection</li>
            <li>Try refreshing the page</li>
            <li>Wait a few moments and try again</li>
            <li>Contact support if the problem persists</li>
          </ul>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            Try Again
          </button>
        )}
        {onGoBack && (
          <button
            onClick={onGoBack}
            style={{
              backgroundColor: '#EEEBE1',
              color: '#36433B',
              border: '1px solid #D2CDBF',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← Go Back
          </button>
        )}
      </div>
    </div>
  );
};

// Add CSS keyframes for animations
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  @keyframes slideDown {
    from { transform: translateY(-100%); }
    to { transform: translateY(0); }
  }
`;
document.head.appendChild(styleSheet);

export default {
  OfflineBanner,
  ErrorDisplay,
  LoadingWithError,
  ConnectionStatus,
  RetryButton,
  NetworkErrorCard
};
